/**
 * Theme Toggle Component
 * Provides UI for switching between light/dark/system/auto themes
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';

const ThemeToggle = ({ showLabel = true, className = '' }) => {
  const { themeMode, resolvedTheme, isDark, setThemeMode, THEMES } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Theme options with icons and labels
  const themeOptions = [
    {
      mode: THEMES.LIGHT,
      label: 'Light',
      icon: 'ri-sun-line',
      description: 'Always light mode'
    },
    {
      mode: THEMES.DARK,
      label: 'Dark',
      icon: 'ri-moon-line',
      description: 'Always dark mode'
    },
    {
      mode: THEMES.SYSTEM,
      label: 'System',
      icon: 'ri-computer-line',
      description: 'Follow system preference'
    },
    {
      mode: THEMES.AUTO,
      label: 'Auto',
      icon: 'ri-time-line',
      description: 'Light 6AM-6PM, Dark 6PM-6AM'
    }
  ];

  // Get current theme option
  const currentOption = themeOptions.find(opt => opt.mode === themeMode) || themeOptions[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle theme selection
  const handleSelectTheme = (mode) => {
    setThemeMode(mode);
    setIsOpen(false);
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={`theme-toggle-wrapper ${className}`} ref={dropdownRef}>
      <button
        className="theme-toggle-btn"
        onClick={toggleDropdown}
        aria-label="Toggle theme"
        aria-expanded={isOpen}
        title={`Current: ${currentOption.label} (${isDark ? 'Dark' : 'Light'})`}
      >
        <i className={isDark ? 'ri-moon-fill' : 'ri-sun-fill'} />
        {showLabel && (
          <span className="toggle-label d-none d-md-inline-block ml-1">
            {currentOption.label}
          </span>
        )}
        <i className="ri-arrow-down-s-line ml-1" />
      </button>

      {isOpen && (
        <div className="theme-dropdown">
          <div className="theme-dropdown-header">
            <span>Appearance</span>
            <span className="current-theme-badge">
              {isDark ? 'Dark' : 'Light'}
            </span>
          </div>

          <div className="theme-options">
            {themeOptions.map((option) => (
              <button
                key={option.mode}
                className={`theme-option ${themeMode === option.mode ? 'active' : ''}`}
                onClick={() => handleSelectTheme(option.mode)}
              >
                <div className="option-icon">
                  <i className={option.icon} />
                </div>
                <div className="option-content">
                  <span className="option-label">{option.label}</span>
                  <span className="option-desc">{option.description}</span>
                </div>
                {themeMode === option.mode && (
                  <i className="ri-check-line option-check" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <style jsx="true">{`
        .theme-toggle-wrapper {
          position: relative;
          display: inline-flex;
          align-items: center;
          height: 100%;
        }

        .theme-toggle-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.25rem;
          padding: 0.5rem 0.75rem;
          background: transparent;
          border: none;
          border-radius: var(--radius-md, 8px);
          color: var(--topbar-icon, #636e75);
          cursor: pointer;
          font-size: 1.25rem;
          transition: all 0.15s ease;
          height: 40px;
          min-width: 40px;
        }

        .theme-toggle-btn:hover {
          background: rgba(0, 0, 0, 0.05);
          color: var(--brand-accent, #5664d2);
        }

        .theme-toggle-btn i {
          font-size: 1.125rem;
        }

        .toggle-label {
          font-weight: 500;
        }

        .theme-dropdown {
          position: absolute;
          top: calc(100% + 0.5rem);
          right: 0;
          min-width: 240px;
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg);
          z-index: var(--z-dropdown);
          overflow: hidden;
          animation: slideDown 0.15s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .theme-dropdown-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid var(--border-color);
          font-weight: 600;
          color: var(--text-primary);
        }

        .current-theme-badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          background: var(--bg-hover);
          border-radius: var(--radius-full);
          font-weight: 500;
          color: var(--text-secondary);
        }

        .theme-options {
          padding: 0.5rem;
        }

        .theme-option {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          width: 100%;
          padding: 0.75rem;
          background: transparent;
          border: none;
          border-radius: var(--radius-md);
          cursor: pointer;
          text-align: left;
          transition: all var(--transition-fast);
        }

        .theme-option:hover {
          background: var(--bg-hover);
        }

        .theme-option.active {
          background: rgba(var(--brand-accent-rgb), 0.1);
        }

        .option-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: var(--bg-secondary);
          border-radius: var(--radius-md);
          color: var(--text-secondary);
          font-size: 1.125rem;
        }

        .theme-option.active .option-icon {
          background: var(--brand-accent);
          color: #fff;
        }

        .option-content {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .option-label {
          font-weight: 500;
          color: var(--text-primary);
        }

        .option-desc {
          font-size: 0.75rem;
          color: var(--text-muted);
        }

        .option-check {
          color: var(--brand-accent);
          font-size: 1.125rem;
        }

        @media (max-width: 576px) {
          .theme-toggle-btn {
            padding: 0.5rem;
          }

          .toggle-label {
            display: none !important;
          }

          .theme-dropdown {
            min-width: 200px;
          }
        }
      `}</style>
    </div>
  );
};

export default ThemeToggle;
